import { useState } from "react";
import {
  Button,
  DialogContentText,
  FormControl,
  MenuItem,
  TextField,
  alpha,
  useTheme,
  Divider,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { KeyOutlined } from "@mui/icons-material";
import DialogBase from "./DialogBase";
import { UtilityService } from "../../services/UtilityService";
import { useOrganisations } from "../../hooks/queries/useOrganisations";
import { useClaimDevice } from "../../hooks/queries/useDevices";
import { logger } from "../../utils/logger";

export default function ClaimCodeDialog(props) {
  const { open, handleClose } = props;
  const { t } = useTranslation("common");
  const theme = useTheme();

  // TanStack Query data
  const { data: organisations = [] } = useOrganisations();

  // TanStack Query mutations
  const claimDeviceMutation = useClaimDevice();

  // Initialize state with first organisation when available
  const [claimCode, setClaimCode] = useState("");
  const [organisationID, setOrganisationID] = useState(
    () => organisations?.[0]?.id
  );

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      "&:hover": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.3),
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      fontWeight: 500,
    },
  };

  async function claimDeviceManually() {
    if (claimCode === "") {
      UtilityService.updateSnackbarMessage(t("common.please_enter_claim_code"));
      return;
    }

    try {
      await claimDeviceMutation.mutateAsync({
        claimCode: claimCode,
        organisationId: organisationID,
      });

      UtilityService.updateSnackbarMessage(
        t("common.device_claimed_successfully")
      );
      setClaimCode(""); // Reset the claim code
      handleClose();
    } catch (error) {
      logger.warn(error);
      UtilityService.updateSnackbarMessage(
        t("common.error_claim_code_not_found")
      );
    }
  }

  return (
    <DialogBase
      open={open}
      handleClose={handleClose}
      title={t("common.enter_claim_code")}
      icon={<KeyOutlined />}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              color:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
              borderColor: alpha(
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
                0.5
              ),
              "&:hover": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                backgroundColor: alpha(
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                  0.05
                ),
              },
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={claimDeviceManually}
            disabled={!claimCode.trim()}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              py: 1,
              px: 3,
              fontWeight: 500,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha("#00c9a7", 0.9)
                    : alpha(theme.palette.primary.main, 0.9),
              },
              "&.Mui-disabled": {
                backgroundColor: alpha(theme.palette.action.disabled, 0.15),
              },
            }}
          >
            {t("common.add")}
          </Button>
        </>
      }
    >
      <DialogContentText sx={{ mb: 2, fontSize: "0.95rem" }}>
        {t("common.claim_intro")}
      </DialogContentText>

      <Stack spacing={3}>
        <FormControl fullWidth>
          <TextField
            id="claimCode"
            label={t("common.claim_code")}
            variant="outlined"
            value={claimCode}
            onChange={(event) => setClaimCode(event.target.value)}
            placeholder={t("common.enter_device_claim_code")}
            sx={textFieldStyle}
          />
        </FormControl>

        <FormControl fullWidth>
          <TextField
            id="organisation_id"
            label={t("common.organisation")}
            variant="outlined"
            select
            value={organisationID || ""}
            onChange={(event) => setOrganisationID(event.target.value)}
            sx={textFieldStyle}
          >
            {organisations && organisations.length > 0 ? (
              organisations.map((organisation) => (
                <MenuItem key={organisation.id} value={organisation.id}>
                  {organisation.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No organizations available</MenuItem>
            )}
          </TextField>
        </FormControl>
      </Stack>

      <Divider />
    </DialogBase>
  );
}
